import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MessageCircle, Send, Loader2 } from "lucide-react";

/**
 * Compact WhatsApp diagnostics panel for the Admin Dashboard.
 * Shows Twilio config status and lets admins send a test message
 * to any opted-in sandbox number without leaving the UI.
 */
export default function AdminWhatsAppPanel() {
  const [status, setStatus] = useState(null);
  const [to, setTo] = useState("");
  const [body, setBody] = useState(
    "Hello from TheHostelNow.com — WhatsApp is wired up ✅"
  );
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/admin/whatsapp/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const send = async () => {
    if (!to.trim()) return toast.error("Enter a phone number (10-digit or +91…)");
    if (!body.trim()) return toast.error("Message body is empty");
    setSending(true);
    try {
      const { data } = await api.post("/admin/whatsapp/test", { to, body });
      toast.success(`Sent · SID ${String(data.message_sid || "").slice(0, 12)}…`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const ready = !!status?.configured;

  return (
    <div
      data-testid="admin-whatsapp-panel"
      className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <div className="grid place-items-center w-10 h-10 rounded-full bg-emerald-600 text-white shadow-sm">
          <MessageCircle size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-stone-900">WhatsApp (Twilio)</span>
            {status === null ? (
              <Badge variant="outline" className="text-[10px]">Checking…</Badge>
            ) : ready ? (
              <Badge className="bg-emerald-600 hover:bg-emerald-600 text-[10px]" data-testid="wa-status-ready">Ready</Badge>
            ) : (
              <Badge variant="outline" className="border-amber-400 text-amber-700 text-[10px]" data-testid="wa-status-off">
                Not configured
              </Badge>
            )}
            {status?.sandbox && (
              <Badge variant="outline" className="border-amber-500 bg-amber-50 text-amber-800 text-[10px]" data-testid="wa-status-sandbox">
                Sandbox mode
              </Badge>
            )}
          </div>
          <div className="text-[11px] text-slate-600 mt-0.5 truncate">
            {ready
              ? `Sender: ${status.sender}${status.admin_number_set ? " · admin alerts ON" : " · admin alerts OFF"}`
              : "Set TWILIO_ACCOUNT_SID + TWILIO_AUTH_TOKEN in backend/.env, then restart."}
          </div>
          {status?.sandbox && (
            <div data-testid="wa-sandbox-warning" className="mt-1.5 text-[11px] rounded-md bg-amber-50 border border-amber-300 text-amber-900 px-2 py-1.5">
              <b>Heads up:</b> you're on the Twilio <b>sandbox</b> number — WhatsApp only reaches phones that have "joined" the sandbox, so broadcasts to owners/students will fail (error 63015). To message everyone, register your own WhatsApp Business sender in Twilio (Messaging → Senders → WhatsApp senders) and update <span className="font-mono">TWILIO_WHATSAPP_FROM</span>.
            </div>
          )}
        </div>
        {ready && (
          <Button
            data-testid="wa-toggle-test-btn"
            size="sm"
            variant={open ? "outline" : "default"}
            className={open ? "" : "bg-emerald-600 hover:bg-emerald-700"}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? "Hide" : "Send test"}
          </Button>
        )}
      </div>

      {open && ready && (
        <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,220px),1fr,auto] md:items-end" data-testid="wa-test-form">
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-slate-500">To</label>
            <Input
              data-testid="wa-test-to"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="+91 98765 43210"
              className="rounded-lg mt-1"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Message</label>
            <Textarea
              data-testid="wa-test-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={2}
              className="rounded-lg mt-1"
            />
          </div>
          <Button
            data-testid="wa-test-send-btn"
            onClick={send}
            disabled={sending}
            className="bg-emerald-600 hover:bg-emerald-700 gap-2"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? "Sending…" : "Send"}
          </Button>
          <div className="md:col-span-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <b>Sandbox:</b> the recipient must have sent "join &lt;code&gt;" to Twilio's WhatsApp
            number in the last 3 days, otherwise Twilio will silently reject.
          </div>
        </div>
      )}
    </div>
  );
}
